import * as React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import { api } from "../api";
import {
  setSong,
  useQueue,
  setCurrentQueueIndex,
  registerAudioElement,
} from "../state";
import { AudioPlayerSong } from "./AudioPlayerSong";
import { RLink } from "../DesignSystem/RLink";

const secondsToReadableString = (totalSeconds: number) => {
  if (isNaN(totalSeconds)) {
    totalSeconds = 0;
  }

  let minutes = String(Math.floor(totalSeconds / 60));
  let seconds = String(Math.floor(totalSeconds % 60));

  if (seconds.length === 1) {
    seconds = "0" + seconds;
  }

  return `${minutes}:${seconds}`;
};

const volumeSliderStyles = `
.audio-volume-slider {
  height: 6px;
  border-radius: 9999px;
  background-color: rgba(15, 23, 42, 0.2);
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
}
.dark .audio-volume-slider {
  background-color: rgba(255, 255, 255, 0.2);
}
.audio-volume-slider:focus {
  outline: none;
}
.audio-volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 16px;
  width: 16px;
  border-radius: 9999px;
  border: 2px solid #f7c564;
  background-color: #1f2937;
  cursor: pointer;
}
.audio-volume-slider::-moz-range-thumb,
.audio-volume-slider::-ms-thumb {
  height: 16px;
  width: 16px;
  border-radius: 9999px;
  border: 2px solid #f7c564;
  background-color: #1f2937;
  cursor: pointer;
}
`;

let sliderStylesInjected = false;

export function AudioPlayer(): JSX.Element {
  const { songs, currentIndex } = useQueue();

  // Audio player state - local to this component
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem("audioPlayerVolume");
    return saved !== null ? parseFloat(saved) : 1;
  });
  const [volumeOn, setVolumeOn] = useState(true);
  const [playlistVisible, setPlaylistVisible] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const volumeInputRef = useRef<HTMLInputElement>(null);

  const [loadedPercentage, setLoadedPercentage] = useState(0);
  const [playedPercentage, setPlayedPercentage] = useState(0);
  const [hoveredPercentage, setHoveredPercentage] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [secondsPlayed, setSecondsPlayed] = useState(0);
  const [secondsTotal, setSecondsTotal] = useState(0);

  // Track if we've recorded this play
  const recordedPlayRef = useRef<number | null>(null);

  // Register audio element with the store
  useEffect(() => {
    registerAudioElement(audioRef.current);
    return () => registerAudioElement(null);
  }, []);

  // Inject slider styles
  useEffect(() => {
    if (typeof document !== "undefined" && !sliderStylesInjected) {
      const style = document.createElement("style");
      style.id = "audio-player-volume-slider";
      style.textContent = volumeSliderStyles;
      document.head.appendChild(style);
      sliderStylesInjected = true;
    }
  }, []);

  // Sync local isPlaying state with audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
    };
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        document.activeElement &&
        (document.activeElement.nodeName === "INPUT" ||
          document.activeElement.nodeName === "TEXTAREA")
      ) {
        return;
      }
      if (e.key === " ") {
        const audio = audioRef.current;
        if (audio) {
          if (audio.paused) {
            audio.play();
          } else {
            audio.pause();
          }
        }
        e.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Record plays and sync currentSong state when playing
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || songs.length === 0) return;

    const entry_id = songs[currentIndex].entry_id;

    if (isPlaying) {
      // Record play if we haven't recorded it yet for this song position
      if (recordedPlayRef.current !== currentIndex) {
        recordedPlayRef.current = currentIndex;
        api.post<"/api/entry/:id/play">(`/api/entry/${entry_id}/play`);
      }
      setSong({ id: entry_id, playing: true });
    } else {
      setSong({ id: entry_id, playing: false });
    }
  }, [isPlaying, songs, currentIndex]);

  // Reset recorded play when song changes
  useEffect(() => {
    recordedPlayRef.current = null;
  }, [currentIndex]);

  // Sync volume to audio element
  useEffect(() => {
    const audio = audioRef.current;
    const volumeInput = volumeInputRef.current;
    if (!audio) return;

    if (volumeOn && volume > 0.02) {
      audio.volume = volume;
      if (volumeInput) volumeInput.value = String(volume * 100);
    } else {
      audio.volume = 0;
      if (volumeInput) volumeInput.value = "0";
    }
  }, [volume, volumeOn]);

  // Persist volume to localStorage
  useEffect(() => {
    localStorage.setItem("audioPlayerVolume", String(volume));
  }, [volume]);

  // Toggle play/pause via the audio element directly
  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      if (audio.paused) {
        audio.play();
      } else {
        audio.pause();
      }
    }
  }, []);

  const handleProgress = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const r = audio.buffered;
    const total = audio.duration;

    if (r.length === 0) return;

    const end = r.end(0);
    const perc = (end / total) * 100;
    setLoadedPercentage(perc);
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const len = audio.duration;
    const current = audio.currentTime;

    setPlayedPercentage((current / len) * 100);
    setSecondsPlayed(Math.floor(audio.currentTime));
    setSecondsTotal(Math.floor(audio.duration));
  };

  const handleSongEnded = () => {
    if (songs.length === 0) return;

    const audio = audioRef.current;
    if (isLooping) {
      if (audio) {
        audio.currentTime = 0;
        audio.play();
      }
    } else {
      if (currentIndex === songs.length - 1) {
        audio?.pause();
      } else {
        setCurrentQueueIndex(currentIndex + 1);
      }
    }
  };

  const handleSeekClick = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    const audio = audioRef.current;
    const seek = e.currentTarget;
    if (!audio || !seek) return;

    const rect = seek.getBoundingClientRect();
    const clientX =
      "touches" in e
        ? (e.touches[0]?.clientX ?? e.changedTouches[0]?.clientX)
        : e.clientX;
    const where = clientX - rect.left;
    const total = rect.width;
    audio.currentTime = (where / total) * audio.duration;
  };

  const handleSeekMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const seek = e.currentTarget;
    const rect = seek.getBoundingClientRect();
    const where = e.clientX - rect.left;
    const total = rect.width;
    setHoveredPercentage((where / total) * 100);
    setHovering(true);
  };

  const handleSeekMouseOut = () => {
    setHovering(false);
  };

  const nextSong = () => {
    if (currentIndex >= songs.length - 1) return;
    setCurrentQueueIndex(currentIndex + 1);
  };

  const prevSong = () => {
    if (currentIndex <= 0) return;
    setCurrentQueueIndex(currentIndex - 1);
  };

  const changeSong = (newSongIndex: number) => {
    setCurrentQueueIndex(newSongIndex);
  };

  const toggleLoop = useCallback(() => {
    setIsLooping((prev) => !prev);
  }, []);

  const togglePlaylist = useCallback(() => {
    setPlaylistVisible((prev) => !prev);
  }, []);

  const handleVolumeChange = useCallback(
    (
      e:
        | React.ChangeEvent<HTMLInputElement>
        | React.MouseEvent<HTMLInputElement>
    ) => {
      const volumeInput = e.currentTarget;
      const vol = Number(volumeInput.value) / 100;
      setVolume(vol <= 0.03 ? 0 : vol);
    },
    []
  );

  const toggleVolume = useCallback(() => {
    setVolumeOn((prev) => !prev);
  }, []);

  const uploaded_url = songs.length > 0 ? songs[currentIndex].uploaded_url : "";

  const currentSong =
    currentIndex >= 0 && currentIndex < songs.length
      ? songs[currentIndex]
      : undefined;

  const songName = currentSong ? currentSong.name : "No song selected";

  return (
    <>
      <audio
        onProgress={handleProgress}
        onEnded={handleSongEnded}
        src={uploaded_url}
        onTimeUpdate={handleTimeUpdate}
        preload="auto"
        ref={audioRef}
        className="hidden"
      />

      {/* Desktop layout */}
      <div className="hidden w-full md:grid md:grid-cols-[minmax(180px,1fr)_minmax(360px,2fr)_minmax(150px,1fr)] md:items-center md:gap-4 text-slate-900 dark:text-white">
        <div className="min-w-[180px]">
          <AudioPlayerSong
            looping={isLooping}
            toggleLoop={toggleLoop}
            changeSongIndex={changeSong}
            songs={songs}
            currentSongIndex={currentIndex}
            playlistVisible={playlistVisible}
            onTogglePlaylist={togglePlaylist}
            hideQueueButton
          />
        </div>

        <div className="flex min-w-[260px] flex-col items-center gap-2 text-sm">
          <div className="flex items-center justify-center gap-2 text-base">
            <button
              type="button"
              onClick={togglePlaylist}
              className="inline-flex hover:scale-110 cursor-pointer h-9 w-9 items-center justify-center text-slate-600 transition hover:text-slate-900 dark:text-white/80 dark:hover:text-white"
              aria-label="Show playlist"
            >
              <i className="fa fa-list-ul" aria-hidden="true"></i>
            </button>

            <button
              type="button"
              onClick={prevSong}
              className="inline-flex hover:scale-110 cursor-pointer h-9 w-9 items-center justify-center text-slate-600 transition hover:text-slate-900 dark:text-white/80 dark:hover:text-white"
              aria-label="Previous song"
            >
              <i className="fa fa-step-backward" aria-hidden="true"></i>
            </button>

            <button
              type="button"
              onClick={togglePlayPause}
              className="inline-flex hover:scale-110 cursor-pointer h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition hover:bg-white/10"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              <i
                className={`fa ${isPlaying ? "fa-pause" : "fa-play"} text-chips-gold`}
                aria-hidden="true"
              ></i>
            </button>

            <button
              type="button"
              onClick={nextSong}
              className="inline-flex hover:scale-110 cursor-pointer h-9 w-9 items-center justify-center text-slate-600 transition hover:text-slate-900 dark:text-white/80 dark:hover:text-white"
              aria-label="Next song"
            >
              <i className="fa fa-step-forward" aria-hidden="true"></i>
            </button>

            <button
              type="button"
              className={`inline-flex cursor-pointer h-9 w-9 items-center justify-center transition hover:scale-110 ${
                isLooping
                  ? "text-chips-gold"
                  : "text-slate-600 hover:text-slate-900 dark:text-white/80 dark:hover:text-white"
              }`}
              onClick={toggleLoop}
              aria-label={isLooping ? "Disable loop" : "Enable loop"}
            >
              <i className="fa fa-repeat" aria-hidden="true"></i>
            </button>
          </div>

          <div className="flex w-full max-w-xl items-center gap-3">
            <span className="text-[0.65rem] font-semibold uppercase tracking-wide tabular-nums text-slate-500 dark:text-white/60">
              {secondsToReadableString(
                hovering
                  ? (hoveredPercentage / 100) * secondsTotal
                  : secondsPlayed
              )}
            </span>
            <div
              onClick={handleSeekClick}
              onTouchStart={handleSeekClick}
              onMouseMove={handleSeekMouseMove}
              onMouseOut={handleSeekMouseOut}
              className="relative h-5 w-full max-w-xl cursor-pointer flex items-center"
            >
              <div className="relative h-1.5 w-full rounded-full bg-slate-200 dark:bg-white/10">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-slate-400 transition-[width] duration-200 dark:bg-white/30"
                  style={{ width: loadedPercentage + "%" }}
                ></div>
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-slate-600 transition-[width] duration-200 dark:bg-white"
                  style={{ width: playedPercentage + "%" }}
                ></div>
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-emerald-400/80"
                  style={{
                    width: hoveredPercentage + "%",
                    opacity: hovering ? 0.8 : 0,
                  }}
                ></div>
              </div>
            </div>
            <span className="text-[0.65rem] font-semibold uppercase tracking-wide tabular-nums text-slate-500 dark:text-white/60">
              {secondsToReadableString(secondsTotal)}
            </span>
          </div>
        </div>

        <div className="flex min-w-[150px] items-center justify-end gap-3">
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-transparent text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:border-white/10 dark:text-white/80 dark:hover:bg-white/10 dark:hover:text-white"
            onClick={toggleVolume}
            aria-label={volumeOn ? "Mute audio" : "Unmute audio"}
          >
            <i
              className={
                volumeOn && volume >= 0.5
                  ? "fa fa-volume-up"
                  : volumeOn && volume >= 0.05
                    ? "fa fa-volume-down"
                    : "fa fa-volume-off"
              }
              aria-hidden="true"
            ></i>
          </button>
          <input
            type="range"
            defaultValue="100"
            onClick={handleVolumeChange}
            onChange={handleVolumeChange}
            ref={volumeInputRef}
            className="audio-volume-slider h-1.5 w-32 cursor-pointer appearance-none rounded-full bg-slate-200 accent-chips-gold dark:bg-white/20"
          />
        </div>
      </div>

      {/* Mobile layout */}
      <div className="flex w-full flex-col items-center gap-1 text-slate-900 dark:text-white md:hidden">
        {/* Hidden AudioPlayerSong for playlist popup only */}
        <div className="absolute bottom-full left-0 right-0">
          <AudioPlayerSong
            looping={isLooping}
            toggleLoop={toggleLoop}
            changeSongIndex={changeSong}
            songs={songs}
            currentSongIndex={currentIndex}
            playlistVisible={playlistVisible}
            onTogglePlaylist={togglePlaylist}
            hideControls
          />
        </div>

        {/* Minimal view when no song selected */}
        {!currentSong && (
          <div className="flex w-full items-center justify-center gap-3 py-1">
            <span className="text-sm text-slate-500 dark:text-white/50">
              No song selected
            </span>
            <button
              type="button"
              onClick={togglePlaylist}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 transition active:bg-slate-100 dark:border-white/15 dark:bg-transparent dark:text-white/80"
              aria-label="Show playlist"
            >
              <i className="fa fa-list-ul text-sm" aria-hidden="true"></i>
            </button>
          </div>
        )}

        {/* Full player when song is selected */}
        {currentSong && (
          <>
            {/* Song name and artist */}
            <div className="w-full text-center">
              <RLink
                to={currentSong.entry_url}
                noUnderline
              >
                {songName.length > 50
                  ? songName.slice(0, 50) + "..."
                  : songName}
              </RLink>
              {currentSong.artists && currentSong.artists.length > 0 && (
                <div className="text-xs text-slate-500 dark:text-white/70 truncate">
                  {currentSong.artists.map((username, i) => (
                    <span key={username}>
                      {i > 0 && ", "}
                      <RLink
                        to={`/user/${username}`}
                        className="hover:text-chips-gold"
                      >
                        {username}
                      </RLink>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Playback bar */}
            <div className="flex w-full items-center gap-2 px-2">
              <span className="text-[0.6rem] font-semibold tabular-nums text-slate-500 dark:text-white/60 min-w-[28px] text-right">
                {secondsToReadableString(secondsPlayed)}
              </span>
              <div
                onClick={handleSeekClick}
                onTouchStart={handleSeekClick}
                className="relative h-5 flex-1 cursor-pointer flex items-center"
              >
                <div className="relative h-1.5 w-full rounded-full bg-slate-200 dark:bg-white/10">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-slate-400 dark:bg-white/30"
                    style={{ width: loadedPercentage + "%" }}
                  ></div>
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-slate-600 dark:bg-white"
                    style={{ width: playedPercentage + "%" }}
                  ></div>
                </div>
              </div>
              <span className="text-[0.6rem] font-semibold tabular-nums text-slate-500 dark:text-white/60 min-w-[28px]">
                {secondsToReadableString(secondsTotal)}
              </span>
            </div>

            {/* Playback controls */}
            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={togglePlaylist}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 transition active:bg-slate-100 dark:border-white/15 dark:bg-transparent dark:text-white/80"
                aria-label="Show playlist"
              >
                <i className="fa fa-list-ul text-sm" aria-hidden="true"></i>
              </button>

              <button
                type="button"
                onClick={prevSong}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 transition active:bg-slate-100 dark:border-white/15 dark:bg-transparent dark:text-white/80"
                aria-label="Previous song"
              >
                <i className="fa fa-step-backward" aria-hidden="true"></i>
              </button>

              <button
                type="button"
                onClick={togglePlayPause}
                className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white transition active:bg-white/10"
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                <i
                  className={`fa ${isPlaying ? "fa-pause" : "fa-play"} text-chips-gold text-lg`}
                  aria-hidden="true"
                ></i>
              </button>

              <button
                type="button"
                onClick={nextSong}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 transition active:bg-slate-100 dark:border-white/15 dark:bg-transparent dark:text-white/80"
                aria-label="Next song"
              >
                <i className="fa fa-step-forward" aria-hidden="true"></i>
              </button>

              <button
                type="button"
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 bg-transparent transition dark:border-white/15 ${
                  isLooping
                    ? "text-chips-gold"
                    : "text-slate-600 active:bg-slate-100 dark:text-white/80"
                }`}
                onClick={toggleLoop}
                aria-label={isLooping ? "Disable loop" : "Enable loop"}
              >
                <i className="fa fa-repeat text-sm" aria-hidden="true"></i>
              </button>
            </div>

            {/* Volume bar */}
            <div className="flex w-full items-center gap-2 px-2">
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 dark:text-white/60"
                onClick={toggleVolume}
                aria-label={volumeOn ? "Mute audio" : "Unmute audio"}
              >
                <i className="fa fa-volume-off" aria-hidden="true"></i>
              </button>
              <input
                type="range"
                defaultValue="100"
                onClick={handleVolumeChange}
                onChange={handleVolumeChange}
                className="audio-volume-slider h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-slate-200 accent-chips-gold dark:bg-white/20"
              />
              <span className="inline-flex h-7 w-7 items-center justify-center text-slate-500 dark:text-white/60">
                <i className="fa fa-volume-up" aria-hidden="true"></i>
              </span>
            </div>
          </>
        )}
      </div>
    </>
  );
}
